import React from 'react';
import { motion } from 'framer-motion';
import { LLink, useLang } from '../i18n';
import { ui } from '../content/site';
import { projectBySlug, projects } from '../content/projects';
import { testimonials } from '../content/testimonials';
import Seo from '../components/Seo';
import ProjectCard from '../components/ProjectCard';
import { Laptop, Phone } from '../components/Devices';
import { Arrow, Reveal, spring } from '../components/motion';

const copy = {
  seoTitle: { en: 'Shopify stores and brand identity', ar: 'متاجر شوبيفاي وهويات بصرية' },
  seoDesc: {
    en: 'ShopFlex Studios designs brand identities and builds Shopify stores for brands ready to grow. Based in Egypt, working worldwide.',
    ar: 'تصمّم ShopFlex Studios الهويات البصرية وتبني متاجر شوبيفاي للعلامات الجاهزة للنمو. مقرّنا مصر ونعمل حول العالم.',
  },
  eyebrow: { en: 'Shopify stores · Brand identity', ar: 'متاجر شوبيفاي · هويات بصرية' },
  title: { en: 'Brands built to sell.', ar: 'علامات تجارية تُبنى لتبيع.' },
  lead: {
    en: 'We design the brand, then build the Shopify store that sells it. One studio, from the first sketch to the first order.',
    ar: 'نصمّم العلامة التجارية، ثم نبني متجر شوبيفاي الذي يبيعها. استوديو واحد، من أول رسمة إلى أول طلب.',
  },
  clients: { en: 'Brands we’ve built for', ar: 'علامات عملنا معها' },
  flagshipEyebrow: { en: 'Featured case study', ar: 'دراسة حالة مميّزة' },
  flagshipTitle: { en: 'Aura Home, from a name to a storefront.', ar: 'Aura Home، من اسم إلى واجهة متجر.' },
  flagshipBody: {
    en: 'Logo, colours, patterns, packaging, a shop front and a Shopify store, designed as one brand.',
    ar: 'شعار وألوان ونقوش وتغليف وواجهة محل ومتجر شوبيفاي، صُمّمت كلها كعلامة واحدة.',
  },
  workTitle: { en: 'Selected work', ar: 'أعمال مختارة' },
  servicesTitle: { en: 'Two things, done properly.', ar: 'خدمتان، نتقنهما.' },
  shopify: { en: 'Shopify stores', ar: 'متاجر شوبيفاي' },
  shopifyBody: {
    en: 'Custom, mobile-first stores that are fast, easy to run and set up to measure what sells.',
    ar: 'متاجر مخصّصة للجوال أولاً، سريعة وسهلة الإدارة ومجهّزة لقياس ما يُباع.',
  },
  identity: { en: 'Brand identity', ar: 'الهوية البصرية' },
  identityBody: {
    en: 'Logo, colour, type, patterns and packaging that make a brand recognisable everywhere it shows up.',
    ar: 'شعار وألوان وخطوط ونقوش وتغليف تجعل العلامة مميّزة أينما ظهرت.',
  },
  shopifyLink: { en: 'Explore Shopify stores', ar: 'اكتشف متاجر شوبيفاي' },
  identityLink: { en: 'Explore brand identity', ar: 'اكتشف الهوية البصرية' },
  processTitle: { en: 'How we work', ar: 'كيف نعمل' },
  wordsTitle: { en: 'In our clients’ words', ar: 'بكلمات عملائنا' },
};

export const steps = [
  {
    title: { en: 'Discovery', ar: 'الاكتشاف' },
    body: { en: 'We learn your brand, your goals and who you’re selling to.', ar: 'نتعرّف على علامتك وأهدافك ومن تبيع لهم.' },
  },
  {
    title: { en: 'Strategy', ar: 'الاستراتيجية' },
    body: { en: 'We plan the look, the structure and what the store needs to do.', ar: 'نخطّط للشكل والبنية وما يجب أن يحقّقه المتجر.' },
  },
  {
    title: { en: 'Design and build', ar: 'التصميم والبناء' },
    body: { en: 'We design it, build it on Shopify and share progress as we go.', ar: 'نصمّمه ونبنيه على شوبيفاي ونشاركك التقدّم أولاً بأول.' },
  },
  {
    title: { en: 'Launch and support', ar: 'الإطلاق والدعم' },
    body: { en: 'We launch, hand over a store you can run, and stay on for support.', ar: 'نطلق المتجر ونسلّمك متجراً تديره بنفسك، ونبقى معك للدعم.' },
  },
];

function Hero() {
  const { t } = useLang();
  const aura = projectBySlug('aura-home');
  return (
    <section className="page pt-14 md:pt-24">
      <div className="mx-auto max-w-4xl text-center">
        <motion.p className="t-eyebrow" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={spring.reveal}>
          {t(copy.eyebrow)}
        </motion.p>
        <motion.h1
          className="t-display mt-5"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...spring.reveal, delay: 0.05 }}
        >
          {t(copy.title)}
        </motion.h1>
        <motion.p
          className="t-lead mx-auto mt-6 max-w-2xl"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...spring.reveal, delay: 0.1 }}
        >
          {t(copy.lead)}
        </motion.p>
        <motion.div
          className="mt-9 flex flex-wrap items-center justify-center gap-x-6 gap-y-4"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...spring.reveal, delay: 0.15 }}
        >
          <LLink to="/contact" className="btn-primary">
            {t(ui.bookCall)}
          </LLink>
          <LLink to="/work" className="link-arrow">
            {t(ui.seeWork)}
            <Arrow />
          </LLink>
        </motion.div>
      </div>

      <motion.div
        className="relative mx-auto mt-14 max-w-5xl pe-[10%] md:mt-20"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...spring.reveal, delay: 0.2 }}
      >
        <Laptop
          src={aura.capture.desktop}
          preview={aura.capture.previewDesktop}
          alt={`${aura.name} – Shopify store`}
          scrollable={false}
          priority
        />
        <Phone
          src={aura.capture.mobile}
          preview={aura.capture.previewMobile}
          alt=""
          scrollable={false}
          priority
          className="absolute -bottom-[3%] end-0 w-[19%] min-w-[80px]"
        />
      </motion.div>
    </section>
  );
}

function Clients() {
  const { t } = useLang();
  return (
    <section className="page mt-24 md:mt-32">
      <Reveal>
        <p className="t-eyebrow text-center">{t(copy.clients)}</p>
        <ul className="mx-auto mt-6 flex max-w-4xl flex-wrap justify-center gap-x-8 gap-y-3 text-xl font-semibold tracking-tight text-ink/70 md:text-2xl" dir="ltr">
          {projects.map((p) => (
            <li key={p.slug}>{p.name}</li>
          ))}
        </ul>
      </Reveal>
    </section>
  );
}

function Flagship() {
  const { t } = useLang();
  return (
    <section className="page mt-24 md:mt-32">
      <Reveal>
        <LLink to="/work/aura-home" className="group block overflow-hidden rounded-4xl bg-aura-green text-aura-sand">
          <div className="grid items-center gap-8 p-8 md:grid-cols-2 md:p-14">
            <div>
              <p className="t-eyebrow text-aura-sand/70">{t(copy.flagshipEyebrow)}</p>
              <h2 className="t-h1 mt-4">{t(copy.flagshipTitle)}</h2>
              <p className="mt-5 max-w-md text-lg text-aura-sand/80">{t(copy.flagshipBody)}</p>
              <span className="link-arrow mt-8 text-aura-sand">
                {t(ui.viewCase)}
                <Arrow />
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <img src="/case-studies/aura-home/brand/totes.webp" width={1583} height={1052} alt="" loading="lazy" className="col-span-2 aspect-[16/9] w-full rounded-2xl object-cover transition-transform duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:scale-[1.02]" />
              <img src="/case-studies/aura-home/brand/pattern-solid.webp" width={556} height={534} alt="" loading="lazy" className="aspect-square w-full rounded-2xl object-cover" />
              <img src="/case-studies/aura-home/brand/boxes.webp" width={1036} height={640} alt="" loading="lazy" className="aspect-square w-full rounded-2xl object-cover" />
            </div>
          </div>
        </LLink>
      </Reveal>
    </section>
  );
}

function SelectedWork() {
  const { t } = useLang();
  const selected = ['les-ashels', 'fimka', 'aurelin', 'q-san-luxury'].map(projectBySlug);
  return (
    <section className="page mt-24 md:mt-32">
      <div className="flex items-end justify-between gap-6">
        <Reveal as="h2" className="t-h2">
          {t(copy.workTitle)}
        </Reveal>
        <LLink to="/work" className="link-arrow shrink-0">
          {t(ui.allWork)}
          <Arrow />
        </LLink>
      </div>
      <div className="mt-10 grid gap-x-8 gap-y-14 md:grid-cols-2">
        {selected.map((p, i) => (
          <Reveal key={p.slug} delay={(i % 2) * 0.08}>
            <ProjectCard project={p} />
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Services() {
  const { t } = useLang();
  const items = [
    { title: copy.shopify, body: copy.shopifyBody, link: copy.shopifyLink, hash: 'shopify' },
    { title: copy.identity, body: copy.identityBody, link: copy.identityLink, hash: 'identity' },
  ];
  return (
    <section className="page mt-24 md:mt-32">
      <Reveal as="h2" className="t-h2 max-w-xl">
        {t(copy.servicesTitle)}
      </Reveal>
      <div className="mt-10 grid gap-4 md:grid-cols-2">
        {items.map((s, i) => (
          <Reveal key={s.hash} delay={i * 0.08} className="flex flex-col rounded-4xl bg-cream-soft p-8 md:p-10">
            <h3 className="t-h3">{t(s.title)}</h3>
            <p className="mt-3 max-w-md text-muted">{t(s.body)}</p>
            <LLink to={`/services#${s.hash}`} className="link-arrow mt-8">
              {t(s.link)}
              <Arrow />
            </LLink>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function Process() {
  const { t } = useLang();
  return (
    <section className="page mt-24 md:mt-32">
      <Reveal as="h2" className="t-h2">
        {t(copy.processTitle)}
      </Reveal>
      <ol className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((step, i) => (
          <Reveal as="li" key={i} delay={i * 0.06} className="border-t border-ink pt-5">
            <span className="text-sm font-semibold tabular-nums text-muted">0{i + 1}</span>
            <h3 className="t-h3 mt-3">{t(step.title)}</h3>
            <p className="mt-2 text-muted">{t(step.body)}</p>
          </Reveal>
        ))}
      </ol>
    </section>
  );
}

function Testimonials() {
  const { t } = useLang();
  return (
    <section className="mt-24 md:mt-32">
      <div className="page">
        <Reveal as="h2" className="t-h2">
          {t(copy.wordsTitle)}
        </Reveal>
      </div>
      {/* Native horizontal scroll: 1:1 tracking and momentum for free. */}
      <ul className="hide-scrollbar mt-10 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-5 px-5 pb-2 sm:scroll-px-8 sm:px-8 xl:scroll-px-[calc((100vw-76rem)/2+2rem)] xl:px-[calc((100vw-76rem)/2+2rem)]">
        {testimonials.map((item) => (
          <li key={item.name} className="flex w-[85%] shrink-0 snap-start flex-col justify-between rounded-4xl bg-cream-soft p-7 sm:w-[24rem]">
            <p className="text-lg leading-relaxed tracking-[-0.01em]">“{t(item.quote)}”</p>
            <p className="mt-6 text-sm text-muted">
              <span className="font-semibold text-ink">{item.name}</span>
              {item.brand && ` · ${item.brand}`}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo title={copy.seoTitle} description={copy.seoDesc} />
      <Hero />
      <Clients />
      <Flagship />
      <SelectedWork />
      <Services />
      <Process />
      <Testimonials />
    </>
  );
}
